import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewChecked
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import type { SubmitSurveyResponseDto, ContactInfoDto } from '../../models/intake-dto';
import { CONTACT_INFO_LIMITS } from '../../models/intake-dto';
import { IntakeService } from '../../services/intake.service';

export type ScoreBand = 'red' | 'yellow' | 'light-green' | 'dark-green';

export interface ScoreLevel {
  min: number;
  max: number;
  label: string;
  band: ScoreBand;
}

@Component({
  selector: 'app-survey-results',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './survey-results.component.html',
  styleUrl: './survey-results.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SurveyResultsComponent implements AfterViewChecked {
  @Input() result: SubmitSurveyResponseDto | null = null;
  /** When true, LLM response is still loading; show "Evaluating..." placeholders. */
  @Input() evaluating = false;
  /** Survey run id (needed to submit contact from results page). */
  @Input() runId = 0;
  /** Token from the submit response (needed for submit-contact). */
  @Input() token = '';

  /** When true, show the contact form on the results page; contact is collected here after submission. */
  @Input() collectContactInfo = false;

  @ViewChild('contactNameInput') contactNameInput?: ElementRef<HTMLInputElement>;

  contact: ContactInfoDto = {};
  contactSaved = false;
  contactSaving = false;
  contactError: string | null = null;
  readonly limits = CONTACT_INFO_LIMITS;

  readonly scoreLevels: ScoreLevel[] = [
    { min: 90, max: 100, label: "You're ahead of the curve", band: 'dark-green' },
    { min: 80, max: 89, label: 'Close to Peak Performance', band: 'light-green' },
    { min: 70, max: 79, label: 'Ready for improvements', band: 'yellow' },
    { min: 0, max: 69, label: 'You have significant room for improvement', band: 'red' }
  ];

  private nameFocused = false;

  constructor(
    private readonly intake: IntakeService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  get hasCallToAction(): boolean {
    return !this.evaluating && !!this.result?.callToActionDisplayText?.trim();
  }

  get displayScore(): number {
    return this.result?.totalScore ?? 0;
  }

  get scoreBand(): ScoreBand {
    const s = this.displayScore;
    if (s >= 90) return 'dark-green';
    if (s >= 80) return 'light-green';
    if (s >= 70) return 'yellow';
    return 'red';
  }

  /** Message for the respondent's score band only (ranges are lookup keys, not displayed). */
  get scoreMessage(): string {
    const s = this.displayScore;
    const level = this.scoreLevels.find((l) => s >= l.min && s <= l.max);
    return level?.label ?? '';
  }

  ngAfterViewChecked(): void {
    if (
      this.nameFocused ||
      !this.collectContactInfo ||
      this.evaluating ||
      this.contactSaved ||
      !this.contactNameInput
    ) {
      return;
    }
    this.contactNameInput.nativeElement.focus();
    this.nameFocused = true;
  }

  saveContact(): void {
    if (!this.contactValid() || this.contactSaving || !this.runId || !this.token) return;
    this.contactError = null;
    this.contactSaving = true;
    this.cdr.markForCheck();
    const payload = {
      surveyRunId: this.runId,
      token: this.token,
      contact: {
        name: this.contact.name?.trim() || undefined,
        email: this.contact.email?.trim() || undefined,
        phoneNumber: this.contact.phoneNumber?.trim() || undefined,
        businessName: this.contact.businessName?.trim() || undefined
      }
    };
    this.intake.submitContact(payload).subscribe({
      next: () => {
        this.contactSaved = true;
        this.contactSaving = false;
        this.cdr.markForCheck();
      },
      error: (err: Error) => {
        this.contactError = err?.message ?? 'Failed to save contact.';
        this.contactSaving = false;
        this.cdr.markForCheck();
      }
    });
  }

  contactValid(): boolean {
    const e = (this.contact.email ?? '').trim();
    const p = (this.contact.phoneNumber ?? '').trim();
    return e.length > 0 || p.length > 0;
  }
}
